import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import useApi from '../../../hooks/useApi'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import { useTranslation } from 'react-i18next'
import Icon from '../../../components/icon'
import styles from './myVolunteeringModal.module.scss'

const StarsRating = ({ statNum }) => {
  if (!statNum) return null

  const stArr = []
  const statNumRounded = Math.round(statNum * 2) / 2

  for (let i = 0; i < 5; i += 1) {
    if (statNumRounded >= i + 1) {
      stArr.push(<Icon name="star" className="starIcon" key={i} />)
    } else if (statNumRounded >= i + 0.5) {
      stArr.push(<Icon name="star-half-stroke" className="starIcon" key={i} />)
    } else {
      stArr.push(<Icon name="star-regular" className="starIcon" key={i} />)
    }
  }

  return <>{stArr}</>
}

export default function MyVolunteeringModal({
  show,
  handleClose,
  statNum,
  reviewCount,
}) {
  const { getMyVolunteering } = useApi()
  const { currentUser } = useCurrentUser()
  const { t } = useTranslation()

  const [volunteers, setVolunteers] = useState([])
  const [expandedVolunteers, setExpandedVolunteers] = useState({})

  const toggleVolunteerExpansion = volunteerId => {
    setExpandedVolunteers(prev => ({
      ...prev,
      [volunteerId]: !prev[volunteerId],
    }))
  }

  useEffect(() => {
    fetchVolunteers()
  }, [])

  const fetchVolunteers = async () => {
  const response = await getMyVolunteering({ userId: currentUser.id })

  if (response?.data?.volunteers) {
    const withRatings = response.data.volunteers
      .map(event => ({
        ...event,
        user_ratings: event.user_ratings?.filter(
          rating => 
            rating.user_id === currentUser.id && 
            rating.user_rating !== null &&
            rating.user_rated_by !== null
        ) ?? []
      }))
      .filter(event => event.user_ratings.length > 0)

    setVolunteers(withRatings)

    const expandedMap = {}
    withRatings.forEach(v => {
      expandedMap[v.event_id] = true
    })
    setExpandedVolunteers(expandedMap)
  }
}

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header closeButton>
        <div className={styles.modalHeader}>
          <h4>{t('users.civic_participation.my_vol')}</h4>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.mainContainer}>
          <h2 className={styles.mainRatingAverage}>
            {statNum ? parseFloat(statNum).toFixed(1) : statNum}
          </h2>
          <span className={styles.stars}>
            <StarsRating key="mainAverage" statNum={statNum} />
          </span>
          <p className="mt-2">{reviewCount}</p>

          {volunteers.map(event => (
            <div key={event.event_id} className={styles.volunteerContainer}>
              <div
                className={styles.volunteerTitleContainer}
                onClick={() => toggleVolunteerExpansion(event.event_id)}
              >
                <div>{event.event_title}</div>
                <div className={styles.volunteerTitleActions}>
                  <div className={styles.volunteerAverageRating}>
                    {event.user_ratings[0].user_rating}
                  </div>
                  <span>
                    <Icon name="star" className={styles.stars} />
                  </span>
                  <span className={styles.expandOrCollapseIcon}>
                    <Icon
                      name={
                        expandedVolunteers[event.event_id]
                          ? 'chevron-up'
                          : 'chevron-down'
                      }
                    />
                  </span>
                </div>
              </div>
              <div
                className={`${styles.ratingDetailsContainer} ${
                  expandedVolunteers[event.event_id]
                    ? styles.expanded
                    : styles.collapsed
                }`}
              >
                {event.user_ratings.map(rating => (
                  <div
                    key={`${event.event_id}-${rating.user_id}`}
                    className={styles.ratingDetails}
                  >
                    <div className={styles.ratingMainContent}>
                      <div>{rating.user_rated_by?.name}</div>
                      <span className={styles.ratingDetailsStars}>
                        <StarsRating statNum={rating.user_rating} />
                      </span>
                    </div>
                    <div className={styles.ratingComment}>
                      {rating.user_comment?.trim() && rating.user_comment}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  )
}
